import Decimal from 'decimal.js';
import { taxYearUtc } from './fiTaxYear';
import type { LedgerEvent } from './types';

/** Normalize Crypto.com export `transaction_kind` the same way as `cryptoComCsv` typeNorm. */
function cdcTypeNorm(e: LedgerEvent): string {
  const raw =
    e.csvMeta?.transactionKind?.trim() ||
    e.csvMeta?.transactionDescription?.trim() ||
    '';
  return raw.toLowerCase().replace(/\s+/g, '_');
}

/**
 * Fiat / bank → Crypto.com (EUR): card / SEPA-style on-ramps only (not crypto deposits or rewards).
 * Excludes `deposit` (often on-chain crypto) and reward types to avoid double-count with income.
 */
export function isCdcFiatOnRampKind(typeNorm: string): boolean {
  return (
    typeNorm === 'fiat_to_crypto' ||
    typeNorm === 'crypto_viban_purchase' ||
    typeNorm === 'viban_purchase'
  );
}

/** Crypto.com → bank / fiat wallet (EUR proceeds), aligned with `isFiatOffRampKind` in cryptoComCsv. */
export function isCdcBankWithdrawalKind(typeNorm: string): boolean {
  if (typeNorm === 'crypto_to_fiat') return true;
  return (
    typeNorm.includes('crypto_viban_exchange') ||
    typeNorm.includes('viban_exchange') ||
    (typeNorm.includes('viban') &&
      typeNorm.includes('exchange') &&
      !typeNorm.includes('purchase'))
  );
}

function filterByTaxPeriod(
  events: LedgerEvent[],
  taxPeriod: number | 'all'
): LedgerEvent[] {
  if (taxPeriod === 'all') return events;
  return events.filter((e) => taxYearUtc(e.ts) === taxPeriod);
}

export type CdcCronosCashflowTotals = {
  cdcFiatInEur: Decimal;
  cdcBankOutEur: Decimal;
  cronosIncomeEur: Decimal;
};

/**
 * Cashflow-style totals for disclosure (does not feed FIFO).
 * - CDC fiat in: EUR from fiat on-ramp row types only.
 * - CDC bank out: EUR from fiat off-ramp / sell-to-fiat style rows (same rules as import).
 * - Cronos income: taxable income FMV on-chain only (`source === cronos`, `kind === income`).
 */
export function computeCdcCronosCashflowTotals(
  events: LedgerEvent[],
  taxPeriod: number | 'all'
): CdcCronosCashflowTotals {
  const ev = filterByTaxPeriod(events, taxPeriod);
  let cdcFiatInEur = new Decimal(0);
  let cdcBankOutEur = new Decimal(0);
  let cronosIncomeEur = new Decimal(0);

  for (const e of ev) {
    if (e.source === 'crypto_com' && e.eurValue != null && e.eurValue.gt(0)) {
      const t = cdcTypeNorm(e);
      if (isCdcFiatOnRampKind(t)) {
        cdcFiatInEur = cdcFiatInEur.plus(e.eurValue);
      }
      if (e.kind === 'disposal' && isCdcBankWithdrawalKind(t)) {
        cdcBankOutEur = cdcBankOutEur.plus(e.eurValue);
      }
    }
    if (
      e.source === 'cronos' &&
      e.kind === 'income' &&
      e.fiTaxCategory === 'TAXABLE_INCOME' &&
      e.eurValue != null &&
      e.eurValue.gt(0)
    ) {
      cronosIncomeEur = cronosIncomeEur.plus(e.eurValue);
    }
  }

  return { cdcFiatInEur, cdcBankOutEur, cronosIncomeEur };
}
